<template>
  <div class="fda-wrapper text-left" v-if="!isMembersOnlyPromotion && (is_loading || availableDates.length > 0)">
    <p class="text-13 font-semibold d-flex align-items-center" :class="promoCode ? 'mb-1' : 'mb-2'">
      <svg class="fda-title-icon mr-1" viewBox="0 0 24 24" width="15" height="15" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      {{ title }}
      <span class="text-disabled font-regular ml-1" v-if="!is_loading && totalNights">
        &middot; {{ totalNights }}-{{ totalNights > 1 ? 'nights' : 'night' }} stay
      </span>
    </p>
    <!-- the suggestions are searched without the promo code, so say so whenever one was applied -->
    <p v-if="promoCode" class="fda-promo-note mb-2">
      {{ $t('hotel.flexible-dates-promo-excluded') }}
    </p>

    <div v-if="is_loading" class="fda-chips d-flex">
      <div class="fda-card fda-card--skeleton flex-shrink-0" v-for="i in 6" :key="i"></div>
    </div>

    <flickity v-else ref="flickity" :options="flickityOptions" class="fda-flickity">
      <button
        v-for="item in decoratedDates"
        :key="item.key"
        type="button"
        class="fda-card carousel-cell flex-shrink-0"
        :class="{
          'fda-card--disabled': !item.is_available,
          'fda-card--best': item.isBest,
        }"
        :disabled="!item.is_available"
        @click="selectDate(item)">
        <span class="fda-card-head d-flex align-items-center justify-content-between">
          <span class="fda-card-weekday">
            <span v-if="item.is_past" class="fda-card-earlier">Earlier &middot; </span>{{ item.weekday }}
          </span>
          <span class="fda-card-weekend" v-if="item.isWeekend && !item.is_past">weekend</span>
        </span>

        <span class="fda-card-body d-block">
          <span class="fda-card-range d-block">{{ item.rangeLabel }}</span>
        </span>

        <span class="fda-card-foot d-block">
          <template v-if="item.is_available && mode === 'SHOW_LOWEST_PRICE'">
            <span class="fda-card-price" :class="{ 'fda-card-price--best': item.isBest }">
              from {{ $formatCurrency(item.lowest_price) }}
            </span>
            <span class="fda-card-badge fda-card-badge--best d-block" v-if="item.isBest">&#9733; Best price</span>
            <span class="fda-card-badge fda-card-badge--urgent d-block" v-else-if="item.isUrgent">
              Only {{ item.availability }} left
            </span>
          </template>
          <template v-else-if="item.is_available && mode === 'SHOW_AVAILABILITY'">
            <span
              class="fda-card-price"
              :class="{ 'fda-card-price--urgent': item.isUrgent }">
              {{ item.isUrgent ? 'Only ' : '' }}{{ item.availability }} room{{ item.availability > 1 ? 's' : '' }} left
            </span>
          </template>
          <span class="fda-card-price text-disabled" v-else>Not Available</span>
        </span>
      </button>
    </flickity>

    <p v-if="!is_loading && isPromoCodeNotFound" class="fda-promo-not-found d-flex align-items-center mt-2 mb-0">
      <svg class="flex-shrink-0 mr-1" viewBox="0 0 24 24" width="14" height="14" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {{ $t('hotel.flexible-dates-promo-not-found', { code: promoCode }) }}
    </p>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { get } from '@/services/index.js';
import { resolveUsablePromoCode } from '@/utils/bePromoCode.js';
import { withPromotionFilter } from '@/utils/bePromotionFilter.js';
import Flickity from 'vue-flickity';
import moment from 'moment';

// Rooms remaining at or below this count are shown with an "Only N left" urgency accent.
// UI display convention, not a business cap — safe to tune.
const LOW_AVAILABILITY_THRESHOLD = 5;

// Card width (140px) plus its right gutter (10px), mirroring the .fda-card styles below.
const CELL_WIDTH_PX = 150;

// Flickity's wrapAround teleports cells to the far side to fill the track. With too few
// cells to span the viewport twice it mispositions them, leaving gaps and out-of-order
// dates, so wrapping stays off until the filtered list is long enough.
const WRAP_AROUND_VIEWPORT_MULTIPLIER = 2;

export default {
  name: 'FlexibleDateAlternatives',
  components: {
    Flickity,
  },
  props: {
    // the map popup lists these property-wide suggestions next to a single room
    // type, so it overrides the copy with something it can actually promise
    title: {
      type: String,
      default: 'Try these available dates instead',
    },
    // narrows the suggestions to one room type; left empty the API answers for
    // the whole property, which is what the result list asks for
    roomTypeId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      is_loading: true,
      mode: 'SHOW_LOWEST_PRICE',
      totalNights: 0,
      dates: [],
      promoCode: '',
      isPromoCodeNotFound: false,
      flickityOptions: {
        initialIndex: 0,
        prevNextButtons: false,
        pageDots: false,
        draggable: true,
        wrapAround: false,
        cellAlign: 'left',
        contain: true,
        autoPlay: 3500,
        pauseAutoPlayOnHover: true,
      },
    };
  },
  computed: {
    // a guest who is not signed in cannot book a membership promotion, so its dates are no use to them
    isMembersOnlyPromotion() {
      return this.$store.getters.isBePromotionFilterMembership && !this.$store.getters.isLoggedIn;
    },
    availableDates() {
      return this.dates.filter((item) => item.is_available);
    },
    cheapestAvailablePrice() {
      const availablePrices = this.availableDates.map((item) => item.lowest_price);
      return availablePrices.length > 0 ? Math.min(...availablePrices) : null;
    },
    uniqueCheapestDate() {
      if (this.mode !== 'SHOW_LOWEST_PRICE' || this.cheapestAvailablePrice === null) return null;
      const matches = this.availableDates.filter(
        (item) => item.lowest_price === this.cheapestAvailablePrice
      );
      return matches.length === 1 ? matches[0] : null;
    },
    decoratedDates() {
      return this.availableDates.map((item) => {
        const checkIn = moment(item.check_in_date);
        const hasAvailability = typeof item.availability === 'number' && item.availability > 0;
        return {
          key: `${item.check_in_date}_${item.check_out_date}`,
          weekday: checkIn.format('ddd'),
          rangeLabel: this.formatRange(item),
          isWeekend: checkIn.day() === 0 || checkIn.day() === 6,
          isBest: this.uniqueCheapestDate === item,
          isUrgent: item.is_available && hasAvailability && item.availability <= LOW_AVAILABILITY_THRESHOLD,
          is_available: item.is_available,
          is_past: item.is_past,
          lowest_price: item.lowest_price,
          availability: item.availability,
          check_in_date: item.check_in_date,
          check_out_date: item.check_out_date,
        };
      });
    },
  },
  watch: {
    isMembersOnlyPromotion(isMembersOnly) {
      if (!isMembersOnly) this.loadFlexibleDates();
    },
    decoratedDates() {
      this.$nextTick(() => {
        if (!this.$refs.flickity) return;
        this.$refs.flickity.reloadCells();
        this.$refs.flickity.resize();
      });
    },
  },
  mounted() {
    this.loadFlexibleDates();
    this.checkPromoCode();
  },
  methods: {
    ...mapGetters(['getBeFilter', 'getMerchant', 'getBeSortBy', 'getBePromotionFilterId']),
    loadFlexibleDates() {
      if (this.isMembersOnlyPromotion) {
        this.is_loading = false;
        this.$emit('dates-loaded', []);
        return;
      }
      this.is_loading = true;
      get('CORE_HOTEL', 'search-room/flexible-dates', this.getFlexibleDatesParams()).then((response) => {
        let res = response.data;
        if (res.success) {
          this.mode = res.data.mode;
          this.totalNights = res.data.total_nights;
          this.dates = res.data.dates;
          this.flickityOptions.initialIndex = this.getInitialIndex();
          this.flickityOptions.wrapAround = this.canWrapAround();
        } else {
          this.dates = [];
        }
        this.is_loading = false;
        this.$emit('dates-loaded', this.availableDates);
      }).catch(() => {
        this.dates = [];
        this.is_loading = false;
        this.$emit('dates-loaded', this.availableDates);
      });
    },
    getFlexibleDatesParams() {
      const { guest: { adult, child, infant }, date } = this.getBeFilter();
      const merchant = this.getMerchant();
      const params = {
        merchant_id: merchant.id,
        check_in_date: this.$formatDateApi(date.check_in),
        check_out_date: this.$formatDateApi(date.check_out),
        adult,
        child: merchant.search_room_is_show_filter_pax_child ? child : 0,
        infant: merchant.search_room_is_show_filter_pax_infant ? infant : 0,
        sort_by: this.getBeSortBy(),
      };
      if (this.$isNotEmptyV2(this.roomTypeId)) params.room_type_id = this.roomTypeId;
      // a promo_id link only suggests stays its promotion accepts, like the room list itself
      return withPromotionFilter(params, this.getBePromotionFilterId());
    },
    checkPromoCode() {
      this.promoCode = this.getBeFilter().promo_code || '';
      if (!this.promoCode) return;
      resolveUsablePromoCode(this.getMerchant().id, this.promoCode).then((usablePromoCode) => {
        this.isPromoCodeNotFound = usablePromoCode === '';
      });
    },
    getInitialIndex() {
      const index = this.availableDates.findIndex((item) => !item.is_past);
      return index === -1 ? 0 : index;
    },
    canWrapAround() {
      const viewportWidth = this.$el ? this.$el.clientWidth : 0;
      if (!viewportWidth) return false;
      const trackWidth = this.decoratedDates.length * CELL_WIDTH_PX;
      return trackWidth >= viewportWidth * WRAP_AROUND_VIEWPORT_MULTIPLIER;
    },
    formatRange(item) {
      const checkIn = moment(item.check_in_date);
      const checkOut = moment(item.check_out_date);
      const sameMonth = checkIn.format('MMM') === checkOut.format('MMM');
      return sameMonth
        ? `${checkIn.format('D')} – ${checkOut.format('D MMM')}`
        : `${checkIn.format('D MMM')} – ${checkOut.format('D MMM')}`;
    },
    selectDate(item) {
      if (!item.is_available) return;
      this.$emit('select-date', {
        check_in_date: this.$formatDateApi(item.check_in_date, true),
        check_out_date: this.$formatDateApi(item.check_out_date, true),
      });
    },
  },
};
</script>
<style scoped>
.fda-title-icon {
  color: var(--merchant-primary-color, #1c2b36);
  flex-shrink: 0;
}
.fda-promo-note {
  font-size: 12px;
  color: #7f7f7f;
}
.fda-promo-not-found {
  font-size: 12px;
  font-weight: 600;
  color: #c0392b;
}
.fda-chips {
  gap: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.fda-chips::-webkit-scrollbar {
  display: none;
}
.fda-flickity >>> .flickity-viewport {
  transition: height 0.2s;
}

.fda-card {
  width: 140px;
  margin-right: 10px;
  border-radius: 12px;
  overflow: hidden;
  white-space: nowrap;
  border: 1.5px solid #e0e8ef;
  background: #fff;
  font-family: inherit;
  text-align: left;
  padding: 0;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.fda-card:not(:disabled):hover {
  border-color: var(--merchant-primary-color, #1c2b36);
  box-shadow: 0 6px 16px rgba(28, 43, 54, 0.12);
  transform: translateY(-2px);
}

.fda-card-head {
  padding: 6px 10px;
  background: rgba(28, 43, 54, 0.05);
  background: color-mix(in srgb, var(--merchant-primary-color, #1c2b36) 8%, #fff);
  border-bottom: 1px solid #eef2f6;
}
.fda-card-weekday {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--merchant-primary-color, #1c2b36);
}
.fda-card-earlier {
  font-weight: 600;
  color: #7f7f7f;
}
.fda-card-weekend {
  font-size: 8.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #b26a00;
  background: #fff3e0;
  padding: 2px 6px;
  border-radius: 999px;
}

.fda-card-body {
  padding: 8px 10px 6px;
}
.fda-card-range {
  font-size: 15px;
  font-weight: 600;
  color: #1c2b36;
  line-height: 1.15;
}

.fda-card-foot {
  padding: 0 10px 9px;
}
.fda-card-price {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--merchant-primary-color, #1c2b36);
}
.fda-card-price--best {
  color: #1a7f37;
}
.fda-card-price--urgent {
  color: #c0392b;
}
.fda-card-badge {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-top: 3px;
}
.fda-card-badge--best {
  color: #1a7f37;
}
.fda-card-badge--urgent {
  color: #c0392b;
}

.fda-card--best {
  border-color: #1a7f37;
  background: #f2fbf4;
}
.fda-card--best .fda-card-head {
  background: #e4f6e9;
  border-bottom-color: #d3efda;
}
.fda-card--best .fda-card-weekday {
  color: #1a7f37;
}

.fda-card--disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.fda-card--disabled .fda-card-head {
  background: #f4f6f8;
}

.fda-card--skeleton {
  width: 140px;
  height: 84px;
  margin-right: 10px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: fda-skeleton-loading 1.4s ease infinite;
}
@keyframes fda-skeleton-loading {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/FlexibleDateAlternatives.vue